import { create } from 'zustand'

type PartnerButton = {
  text: string
  link: string
}

export type PartnerSlide = {
  id: string | number
  title: string
  content: string
  coverPhotosIds: string[]
  coverPhotoPreviewUrl?: string
  buttons: PartnerButton[]
  layout?: 'left' | 'right'
}

type PartnerContentState = {
  slides: PartnerSlide[]
  currentSlide: number
  reorderSlidesModal: boolean

  setSlides: (slides: PartnerSlide[]) => void
  setCurrentSlide: (index: number) => void
  setReorderSlidesModal: (open: boolean) => void

  addSlide: () => void
  removeSlide: (slideId: string | number) => void
  updateSlide: (
    slideId: string | number,
    update: Partial<Omit<PartnerSlide, 'id'>> & { id?: string | number },
  ) => void
}

const defaultButtons: PartnerButton[] = [{ text: '', link: '' }]

const createEmptySlide = (): PartnerSlide => ({
  id: `local-${Date.now()}`,
  title: '',
  content: '',
  coverPhotosIds: [],
  coverPhotoPreviewUrl: '',
  buttons: defaultButtons,
  layout: 'left',
})

const partnerContent = (set: any): PartnerContentState => ({
  slides: [],
  currentSlide: 0,
  reorderSlidesModal: false,

  setSlides: (slides: PartnerSlide[]) => set({ slides }),

  setCurrentSlide: (index: number) =>
    set(() => ({ currentSlide: Math.max(0, index) })),

  setReorderSlidesModal: (open: boolean) => set({ reorderSlidesModal: open }),

  addSlide: () =>
    set((state: PartnerContentState) => ({
      slides: [...state.slides, createEmptySlide()],
    })),

  removeSlide: (slideId: string | number) =>
    set((state: PartnerContentState) => ({
      slides: state.slides.filter(s => s.id !== slideId),
      currentSlide:
        state.currentSlide >= state.slides.length - 1
          ? Math.max(0, state.currentSlide - 1)
          : state.currentSlide,
    })),

  updateSlide: (slideId, update) =>
    set((state: PartnerContentState) => ({
      slides: state.slides.map(s =>
        s.id === slideId
          ? {
              ...s,
              id: update.id ?? s.id,
              title: update.title ?? s.title,
              content: update.content ?? s.content,
              coverPhotosIds: update.coverPhotosIds ?? s.coverPhotosIds,
              coverPhotoPreviewUrl:
                update.coverPhotoPreviewUrl ?? s.coverPhotoPreviewUrl,
              buttons: update.buttons ?? s.buttons,
              layout: update.layout ?? s.layout ?? 'left',
            }
          : s,
      ),
    })),
})

export const partnerContentStore = create<PartnerContentState>(partnerContent)
